import { formatAmount, formatDate, formatHKD } from "./calculations";
import type { BillingProfile, Invoice, LineItem } from "./types";
import type { CSSProperties, Ref } from "react";

interface InvoicePreviewProps {
  invoice: Invoice;
  profile: BillingProfile;
  zoom: "fit" | "75" | "100" | "125";
  exportRef?: Ref<HTMLDivElement>;
  logoUrl?: string;
  receiptImageUrls?: Record<string, string>;
}

const labels = {
  "zh-HK": {
    invoice: "發票",
    from: "開票者",
    billTo: "客戶",
    issueDate: "發票日期",
    dueDate: "到期日",
    currency: "貨幣",
    purchaseOrderNumber: "採購單編號",
    projectReference: "項目／工作編號",
    description: "項目說明",
    qty: "數量",
    rate: "單價",
    amount: "金額",
    subtotal: "小計",
    discount: "折扣",
    total: "總額",
    paid: "已收",
    balance: "尚欠",
    notes: "備註",
    payment: "付款資料",
    continued: "續頁",
    receiptAppendix: "收據附件",
    originalReceipt: "原始收據圖片",
    secureImageLoading: "正在載入私人收據圖片…",
  },
  en: {
    invoice: "INVOICE",
    from: "FROM",
    billTo: "BILL TO",
    issueDate: "ISSUE DATE",
    dueDate: "DUE DATE",
    currency: "CURRENCY",
    purchaseOrderNumber: "PO NUMBER",
    projectReference: "PROJECT / JOB REF.",
    description: "DESCRIPTION",
    qty: "QTY",
    rate: "RATE",
    amount: "AMOUNT",
    subtotal: "SUBTOTAL",
    discount: "DISCOUNT",
    total: "TOTAL",
    paid: "PAID",
    balance: "BALANCE",
    notes: "NOTES",
    payment: "PAYMENT DETAILS",
    continued: "CONTINUED",
    receiptAppendix: "RECEIPT APPENDIX",
    originalReceipt: "ORIGINAL RECEIPT IMAGE",
    secureImageLoading: "LOADING PRIVATE RECEIPT IMAGE…",
  },
} as const;

function partyLines(party: Invoice["issuer"]): string[] {
  return [
    party.contactName,
    party.address,
    party.email,
    party.phone,
    party.registrationNumber,
  ].filter(Boolean);
}

function chunkItems(items: LineItem[], firstPageCapacity = 12): LineItem[][] {
  if (items.length <= firstPageCapacity) return [items];
  const chunks: LineItem[][] = [items.slice(0, firstPageCapacity)];
  for (let index = firstPageCapacity; index < items.length; index += 18) {
    chunks.push(items.slice(index, index + 18));
  }
  return chunks;
}

export function InvoicePreview({
  invoice,
  profile,
  zoom,
  exportRef,
  logoUrl,
  receiptImageUrls = {},
}: InvoicePreviewProps) {
  const copy = labels[invoice.language];
  const purchaseOrderNumber = invoice.purchaseOrderNumber.trim();
  const projectReference = invoice.projectReference.trim();
  const referenceCount =
    Number(Boolean(purchaseOrderNumber)) + Number(Boolean(projectReference));
  const chunks = chunkItems(invoice.items, referenceCount > 0 ? 10 : 12);
  const receipts = [...invoice.receipts].sort(
    (a, b) =>
      a.position - b.position || a.createdAt.localeCompare(b.createdAt),
  );
  const totalPages = chunks.length + receipts.length;
  const brand = profile.brandColour || "#151515";
  const scale = zoom === "fit" ? 0.66 : Number(zoom) / 100;

  return (
    <div
      className="preview-pages"
      ref={exportRef}
      data-zoom={zoom}
      style={{ "--preview-scale": scale } as CSSProperties}
      aria-label="Live invoice preview"
    >
      {chunks.map((items, pageIndex) => {
        const first = pageIndex === 0;
        const last = pageIndex === chunks.length - 1;
        return (
          <div
            className={`invoice-paper ${invoice.paperSize}`}
            key={`${invoice.id}-${pageIndex}`}
            style={{ "--invoice-brand": brand } as CSSProperties}
            data-page={pageIndex + 1}
          >
            <header className="invoice-head">
              <div>
                {logoUrl ? (
                  <img
                    className="invoice-logo"
                    src={logoUrl}
                    alt={`${profile.displayName} logo`}
                  />
                ) : (
                  <div className="invoice-brand-rule" />
                )}
                <strong>{profile.displayName || "BUSINESS NAME"}</strong>
              </div>
              <div className="invoice-title">
                <h2>{copy.invoice}</h2>
                <p>
                  {invoice.number || "INV-000"}
                  {!first && ` · ${copy.continued}`}
                </p>
              </div>
            </header>

            {first && (
              <>
                <div className="party-grid">
                  <section>
                    <p className="invoice-label">{copy.from}</p>
                    <h3>{invoice.issuer.name || profile.displayName}</h3>
                    {partyLines(invoice.issuer).map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </section>
                  <section>
                    <p className="invoice-label">{copy.billTo}</p>
                    <h3>{invoice.client.name || "CLIENT NAME"}</h3>
                    {partyLines(invoice.client).map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </section>
                </div>
                <div className="invoice-meta-grid">
                  <div>
                    <span>{copy.issueDate}</span>
                    <strong>{formatDate(invoice.issueDate)}</strong>
                  </div>
                  <div>
                    <span>{copy.dueDate}</span>
                    <strong>{formatDate(invoice.dueDate)}</strong>
                  </div>
                  <div>
                    <span>{copy.currency}</span>
                    <strong>HKD</strong>
                  </div>
                </div>
                {referenceCount > 0 && (
                  <div
                    className={`invoice-reference-grid ${
                      referenceCount === 2 ? "two" : "one"
                    }`}
                  >
                    {purchaseOrderNumber && (
                      <div>
                        <span>{copy.purchaseOrderNumber}</span>
                        <strong>{purchaseOrderNumber}</strong>
                      </div>
                    )}
                    {projectReference && (
                      <div>
                        <span>{copy.projectReference}</span>
                        <strong>{projectReference}</strong>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            <table className="invoice-items-preview">
              <thead>
                <tr>
                  <th>{copy.description}</th>
                  <th>{copy.qty}</th>
                  <th>{copy.rate}</th>
                  <th>{copy.amount}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.description || "—"}</td>
                    <td>{item.quantity}</td>
                    <td>{formatAmount(item.rateMinor)}</td>
                    <td>{formatAmount(item.amountMinor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {last && (
              <>
                <div className="invoice-total-block">
                  <div>
                    <span>{copy.subtotal}</span>
                    <strong>{formatAmount(invoice.subtotalMinor)}</strong>
                  </div>
                  {invoice.discountMinor > 0 && (
                    <div>
                      <span>{copy.discount}</span>
                      <strong>-{formatAmount(invoice.discountMinor)}</strong>
                    </div>
                  )}
                  <div className="invoice-grand-total">
                    <span>{copy.total}</span>
                    <strong>{formatHKD(invoice.totalMinor)}</strong>
                  </div>
                  {invoice.paidMinor > 0 && (
                    <>
                      <div>
                        <span>{copy.paid}</span>
                        <strong>{formatAmount(invoice.paidMinor)}</strong>
                      </div>
                      <div>
                        <span>{copy.balance}</span>
                        <strong>{formatAmount(invoice.balanceMinor)}</strong>
                      </div>
                    </>
                  )}
                </div>

                {(invoice.clientNotes || invoice.paymentInstructions) && (
                  <div className="invoice-notes-grid">
                    <section>
                      <p className="invoice-label">{copy.notes}</p>
                      <p>{invoice.clientNotes || "—"}</p>
                    </section>
                    <section>
                      <p className="invoice-label">{copy.payment}</p>
                      <p>{invoice.paymentInstructions || "—"}</p>
                    </section>
                  </div>
                )}
              </>
            )}

            <footer className="invoice-footer">
              <span>
                {[profile.email, profile.website, profile.registrationNumber]
                  .filter(Boolean)
                  .join(" · ") || profile.displayName}
              </span>
              <span>
                {pageIndex + 1} / {totalPages}
              </span>
            </footer>
          </div>
        );
      })}
      {receipts.map((receipt, receiptIndex) => {
        const pageNumber = chunks.length + receiptIndex + 1;
        const receiptUrl = receiptImageUrls[receipt.id];
        return (
          <div
            className={`invoice-paper receipt-page ${invoice.paperSize}`}
            key={receipt.id}
            style={{ "--invoice-brand": brand } as CSSProperties}
            data-page={pageNumber}
            data-receipt-id={receipt.id}
          >
            <header className="receipt-page-head">
              <div>
                <div className="invoice-brand-rule" />
                <strong>{profile.displayName || "BUSINESS NAME"}</strong>
              </div>
              <div className="invoice-title">
                <h2>{copy.receiptAppendix}</h2>
                <p>{invoice.number || "INV-000"}</p>
              </div>
            </header>

            <section className="receipt-page-content">
              <div className="receipt-page-meta">
                <span>{copy.originalReceipt}</span>
                <strong title={receipt.fileName}>{receipt.fileName}</strong>
              </div>
              <div className="receipt-image-frame">
                {receiptUrl ? (
                  <img
                    src={receiptUrl}
                    alt={`${copy.originalReceipt}: ${receipt.fileName}`}
                    data-export-image="receipt"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div
                    className="receipt-image-pending"
                    data-export-image-pending="true"
                    role="status"
                  >
                    {copy.secureImageLoading}
                  </div>
                )}
              </div>
            </section>

            <footer className="invoice-footer">
              <span>
                {[profile.email, profile.website, profile.registrationNumber]
                  .filter(Boolean)
                  .join(" · ") || profile.displayName}
              </span>
              <span>
                {pageNumber} / {totalPages}
              </span>
            </footer>
          </div>
        );
      })}
    </div>
  );
}
